import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";
import {
  TrustChips,
  StoreMatrix,
  DataFlow,
  RetentionTable,
  ReviewerCard,
} from "@/components/trust/trust-blocks";

const TITLE = "Security at Pyng";
const DESC =
  "How Pyng protects your relationship notes, voice memos, and connected accounts — isolation, encryption, access control, and deletion.";

export const Route = createFileRoute("/security")({
  head: () => ({
    meta: [
      { title: "Security — Pyng" },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SecurityPage,
});

function SecurityPage() {
  return (
    <LegalPage title={TITLE} eyebrow="Trust" lastUpdated="August 2026">
      <p>
        Pyng holds the most sensitive thing a professional owns: private context about the people they
        work with. This page describes, plainly, how that data is protected today. We describe only what
        is actually in place — no certifications we do not hold.
      </p>

      <TrustChips />

      <h2>What Pyng stores, and what it never touches</h2>
      <StoreMatrix />

      <h2>Where your data travels</h2>
      <DataFlow />



      <h2>Data isolation</h2>
      <p>
        Every table that holds your data enforces row-level security at the database, not just in the
        application. Each row carries the owner's account ID, and the database itself rejects any read or
        write that does not match the signed-in account. A bug in the app cannot hand one customer another
        customer's contacts, notes, or memos, because the isolation is enforced one layer below the app.
      </p>

      <h2>Encryption</h2>
      <ul>
        <li>All traffic to and from Pyng is encrypted in transit over TLS.</li>
        <li>The database and file storage that hold your notes, transcripts, and voice memo audio are encrypted at rest by our infrastructure provider, including backups.</li>
        <li>Access tokens for Live Context accounts (Google Calendar, Gmail) are additionally encrypted with AES-256-GCM using a key held outside the database, so the stored value is unreadable even to someone browsing the database directly.</li>
        <li>Voice memo audio lives in a private storage bucket that is never publicly readable and is only served through short-lived, per-request signed URLs.</li>
      </ul>
      <p>
        To be precise about a term people use loosely: Pyng is encrypted in transit and at rest, not
        end-to-end encrypted. Our servers can process your content in order to run the Signal Engine and
        write drafts. We say so plainly rather than imply a guarantee we do not provide.
      </p>

      <h2>Who can see your data</h2>
      <ul>
        <li>The people you watch, your notes, memos, and drafts are visible only to your own signed-in account. No other subscriber can see who is on your list, and there is no shared or public directory of people being followed.</li>
        <li>Cross-account reads are blocked at the database layer, not just in the app.</li>
        <li>A small founding team can reach production infrastructure to keep the service running and to investigate incidents. That access is logged, used only when needed, and never used to browse customer content casually.</li>
        <li>Your content is never sold, never shared with other subscribers, and never used to train third-party AI models.</li>
      </ul>

      <h2>Legal process</h2>
      <p>
        We would only disclose customer data where we are legally compelled to do so. If we receive a
        subpoena, court order, or regulatory demand relating to an account, we will tell the affected
        subscriber unless we are legally prohibited from doing so, we will narrow or challenge requests
        that are overbroad, and we will disclose no more than the request actually requires.
      </p>

      <h2>Connected accounts (Live Context)</h2>
      <p>
        Google Calendar and Gmail are connected per person, through Google's own consent screen, with the
        narrowest scopes each feature requires. Pyng never asks for your Google password. You can revoke a
        connection from Settings at any time, which deletes the stored credential on our side as well.
      </p>
      <p>
        Gmail is read as <strong>metadata only</strong> — sender, recipient, subject and timestamp, limited to the
        people already in your orbit. Message bodies are never fetched, stored, or sent to a model. It powers one
        thing: an accurate last touch, and knowing when someone replied to you.
      </p>

      <h2>Access control</h2>
      <ul>
        <li>Two-factor authentication is available on every account from Settings.</li>
        <li>You can sign out of every device from Settings.</li>
        <li>Security-relevant events on your account — sign-ins, exports, deletions, connector changes — are recorded to an activity log you can read, and that the app cannot edit or erase.</li>
        <li>Administrative access to production is limited to the founding team and is not used to read customer content except when you ask us to investigate something.</li>
      </ul>

      <h2>How AI is used</h2>
      <p>
        When Pyng drafts a message, summarizes a relationship, or extracts facts from a voice note, it sends
        the minimum content required for that one request to our AI provider. Those requests are covered by
        commercial terms that prohibit using submitted content to train the provider's models. Your notes,
        memories, and transcripts are never used to train models — ours or anyone else's. Public web pages
        we read on your behalf are fetched only for contacts you added yourself.
      </p>

      <h2>Your data, your call</h2>
      <ul>
        <li>Export everything you have stored, at any time, in one click.</li>
        <li>Delete a memory, a contact, or your entire account. Deleting the account removes your rows and your stored files, including voice memo audio.</li>
        <li>Set voice memos and transcripts to auto-delete after 30, 90, or 365 days.</li>
      </ul>

      <h2>Retention and deletion</h2>
      <RetentionTable />

      <h2>Vendors</h2>
      <p>
        We keep the list of subprocessors that can touch customer data short and public. See{" "}
        <Link to="/subprocessors">our subprocessor list</Link>. Related reading:{" "}
        <Link to="/privacy">Privacy</Link> and <Link to="/terms">Terms</Link>.
      </p>

      <h2>Compliance posture</h2>
      <p>
        Corporate reviewers usually ask one question: what do you hold, and what can you show me today?
        Here is the honest three-part answer.
      </p>
      <p>
        <strong>Certifications held today: none.</strong> Pyng does not hold SOC 2, ISO 27001, or any other
        third-party attestation, and we will not imply otherwise. SOC 2 is an auditor's opinion on operating
        controls over a monitored window, not an encryption standard — so the useful question in the meantime
        is which of those controls already run in production.
      </p>
      <p><strong>Controls already in place — the things an audit would test:</strong></p>
      <ul>
        <li>Tenant isolation enforced at the database layer, below the application.</li>
        <li>TLS in transit; AES-256 at rest for the database, backups, and stored files.</li>
        <li>Connected-account tokens encrypted with a key held outside the database.</li>
        <li>Two-factor authentication and full device sign-out available to every account.</li>
        <li>An append-only account activity log covering sign-ins, exports, deletions, and connector changes.</li>
        <li>Least-privilege production access limited to the founding team, used for operations and incidents.</li>
        <li>Data minimization by design: email metadata only, calendar read-only, no message bodies, no model training.</li>
        <li>A published subprocessor list and a monitored vulnerability contact.</li>
      </ul>
      <p><strong>Roadmap, in the order it will happen:</strong> formalize written policies and access reviews,
        commission a third-party penetration test, complete a SOC 2 Type I readiness assessment, then run the
        Type II observation window. We are not publishing target dates we cannot guarantee — if you need a
        timeline for a specific procurement review, ask and you will get our current honest estimate.
      </p>

      <h2>Reporting a vulnerability</h2>
      <p>
        If you believe you have found a security issue, email{" "}
        <a href="mailto:security@pyng.co">security@pyng.co</a> with enough detail to reproduce it. We will
        acknowledge within two business days. Please do not publicly disclose before we have had a chance to
        fix it, and please do not access, modify, or retain data that is not yours while testing.
      </p>

      <ReviewerCard />

    </LegalPage>
  );
}